/**
 * CR-863: the requests behind one segment of the price change requests chart.
 *
 * Tabs All + one per outcome (the PO line's Change Requests tab's words), the
 * rows newest first, Export to Excel, a pager. The PO number opens the line in
 * a new browser tab.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  priceChangeRequestsAPI,
  type PriceChangeRequestOutcome,
  type PriceChangeRequestRow,
  type PriceChangeRequestsPage,
  type PriceChangeRequestsQuery,
} from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { notifyExportResult, type ExportTruncation } from '../../../lib/exportTruncation';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import ExportTruncationBanner from '../../export/ExportTruncationBanner';
import { StatusBadge } from '../../purchasing/ChangeRequestCard';
import { DrilldownPager } from '../po-lines/DrilldownPager';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 25;
const TABS: Array<PriceChangeRequestOutcome | 'all'> = ['all', 'open', 'accepted', 'declined', 'cancelled'];

interface PriceChangeRequestsModalProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Mar 26 · Ironvale Precision". */
  title: string;
  /** The segment, without outcome or page. */
  query: Omit<PriceChangeRequestsQuery, 'outcome' | 'page' | 'pageSize'>;
}

/**
 * The click-through dialog.
 *
 * @param open - whether the dialog shows
 * @param onClose - called when it closes
 * @param title - period and supplier of the segment
 * @param query - the segment's scope, dates and suppliers
 */
export function PriceChangeRequestsModal({ open, onClose, title, query }: PriceChangeRequestsModalProps) {
  const { formatDate } = useFormatters();
  const [tab, setTab] = useState<PriceChangeRequestOutcome | 'all'>('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PriceChangeRequestsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);

  const outcome = tab === 'all' ? undefined : tab;
  const { scope, periodStart, periodEnd, supplierIds } = query;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    priceChangeRequestsAPI
      .list({ scope, periodStart, periodEnd, supplierIds, outcome, page, pageSize: PAGE_SIZE })
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        console.error('Price change requests failed to load:', err);
        if (!cancelled) setError('The requests could not be loaded. Close this and try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, scope, periodStart, periodEnd, supplierIds, outcome, page]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await priceChangeRequestsAPI.exportXlsx({ scope, periodStart, periodEnd, supplierIds, outcome });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `price_change_requests_${periodStart}_${periodEnd}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setTruncation(notifyExportResult(resp, 'Excel export ready'));
    } catch (err: unknown) {
      console.error('Price change requests export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [scope, periodStart, periodEnd, supplierIds, outcome]);

  const counts = data?.outcome_counts;
  const labels = data?.outcome_labels;
  const rows = data?.results ?? [];
  const shownCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(shownCount / PAGE_SIZE));
  const accepted = counts?.accepted ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* 12 columns: as wide as CR-1421's week list (1800px / 95vw), so Outcome is never behind a scroll. */}
      <DialogContent className="!max-w-[min(1800px,95vw)] w-[min(1800px,95vw)]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>Price change requests: {title}</DialogTitle>
              <DialogDescription>
                {data ? `${data.total.toLocaleString()} request${data.total === 1 ? '' : 's'}, ${accepted.toLocaleString()} accepted` : 'Loading…'}
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || shownCount === 0}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
          <ExportTruncationBanner truncation={truncation} onDismiss={() => setTruncation(null)} />
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b pb-2" role="tablist" aria-label="Outcome">
          {TABS.map((t) => {
            const active = t === tab;
            const label = t === 'all' ? 'All' : (labels?.[t] ?? t);
            const count = t === 'all' ? data?.total : counts?.[t];
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => { setTab(t); setPage(1); }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {label}{count !== undefined ? <span className="ml-1 tabular-nums opacity-80">({count.toLocaleString()})</span> : null}
              </button>
            );
          })}
        </div>

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 && !error ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No requests under this tab.</div>
        ) : (
          <RequestsTable rows={rows} formatDate={formatDate} />
        )}

        {shownCount > PAGE_SIZE && (
          <DrilldownPager page={page} totalPages={totalPages} loading={loading} onPageChange={setPage} idPrefix="pcr" />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface RequestsTableProps {
  rows: PriceChangeRequestRow[];
  formatDate: (value: string) => string;
}

function RequestsTable({ rows, formatDate }: RequestsTableProps) {
  const money = (value: string, currency: string) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
    } catch {
      return `${n.toFixed(2)} ${currency}`;
    }
  };
  return (
    <div className="overflow-x-auto max-h-[60vh]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-background">
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 px-2 font-medium whitespace-nowrap">Requested on</th>
            <th className="py-2 px-2 font-medium">Supplier</th>
            <th className="py-2 px-2 font-medium">Site</th>
            <th className="py-2 px-2 font-medium">PO</th>
            <th className="py-2 px-2 font-medium text-right">Line</th>
            <th className="py-2 px-2 font-medium">Part</th>
            <th className="py-2 px-2 font-medium">Description</th>
            <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Price was</th>
            <th className="py-2 px-2 font-medium text-right whitespace-nowrap">Price asked</th>
            <th className="py-2 px-2 font-medium text-right">Change</th>
            <th className="py-2 px-2 font-medium">Reason</th>
            <th className="py-2 px-2 font-medium">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b last:border-0">
              <td className="py-2 px-2 whitespace-nowrap">{formatDate(r.requested_on)}</td>
              <td className="py-2 px-2 min-w-[8rem] max-w-[14rem]">{r.supplier_name}</td>
              <td className="py-2 px-2 min-w-[8rem] max-w-[14rem]">{r.site_name}</td>
              <td className="py-2 px-2 whitespace-nowrap">
                {r.po_line_id ? (
                  <a className="text-primary font-medium hover:underline" href={`/purchasing?tab=po_lines&line=${r.po_line_id}`}
                    target="_blank" rel="noopener noreferrer">{r.po_number}</a>
                ) : r.po_number}
              </td>
              <td className="py-2 px-2 text-right tabular-nums">{r.line_number ?? ''}</td>
              <td className="py-2 px-2 whitespace-nowrap">{r.part_number}</td>
              <td className="py-2 px-2 min-w-[10rem]">{r.description}</td>
              <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{money(r.price_was, r.currency)}</td>
              <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{money(r.price_asked, r.currency)}</td>
              <td className={`py-2 px-2 text-right tabular-nums whitespace-nowrap ${
                r.change_pct === null ? '' : r.change_pct >= 0 ? 'text-red-700' : 'text-emerald-700'
              }`}>
                {r.change_pct === null ? '' : `${r.change_pct >= 0 ? '+' : ''}${r.change_pct.toFixed(1)}%`}
              </td>
              <td className="py-2 px-2 min-w-[10rem]">{r.reason}</td>
              <td className="py-2 px-2 whitespace-nowrap"><StatusBadge status={r.outcome} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
